<template>
  <div class="m-t-xs">
    <!-- Collapsed: one line saying which clock the times above are on. Blue
         when that isn't the coach's own clock, so it doubles as the warning. -->
    <p v-if="!editing" class="text-14 m-y-none" :class="differs ? 'text-blue-700' : 'text-gray-500'" data-cy="time-zone-summary">
      <i class="fa fa-globe m-r-xxs"></i>Time zone: <span class="font-semi">{{ zoneName(effectiveZone) }}</span>
      ({{ sourceLabel }}) &middot;
      <span v-if="differs" data-cy="time-zone-device-note">your device is on {{ zoneName(browserZone) }} &middot;</span>
      <a class="cursor-pointer" @click="editing = true" data-cy="time-zone-change">Change</a>
    </p>
    <div v-else>
      <label class="text-12 m-y-none tracking-wide">Time zone for this event</label>
      <select class="form-control" :value="value || ''" @change="pick($event.target.value)" data-cy="time-zone-select">
        <option value="">{{ defaultOptionLabel }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
      <p class="text-12 text-gray-500 m-t-xxs m-b-none">
        For away events like a tournament in another time zone. Enter times on that clock; everyone's calendar shows them correctly in their own time.
      </p>
    </div>
  </div>
</template>

<script>
import {
  formTimeZone, differsFromBrowser, sameClock, zoneLabel, TIME_ZONE_OPTIONS,
} from '../event_time_zone';

// Per-event time zone override (v-model = IANA zone or null). Collapsed by
// default since most events just follow their location / the coach's clock.
export default {
  name: 'time-zone-picker',
  props: {
    // The event's override; null = no override
    value: { type: String, required: false, default: null },
    // The selected location's own zone, used when there's no override
    locationTimeZone: { type: String, required: false, default: null },
    // When the event happens (moment/ISO), for the device-clock comparison
    at: { required: false, default: null },
  },
  data() {
    return {
      editing: false,
    };
  },
  computed: {
    effectiveZone() {
      return formTimeZone(this.value || this.locationTimeZone);
    },
    // What the event follows with no override: its location, else the team
    defaultZone() {
      return formTimeZone(this.locationTimeZone);
    },
    browserZone() {
      return moment.tz.guess();
    },
    differs() {
      return differsFromBrowser(this.effectiveZone, this.at);
    },
    sourceLabel() {
      if (this.value) {
        return 'set for this event';
      }
      return this.locationTimeZone ? "this location's zone" : 'team default';
    },
    defaultOptionLabel() {
      const source = this.locationTimeZone ? "this location's zone" : 'team default';
      return `${this.zoneName(this.defaultZone)} (${source})`;
    },
    // The default row already covers its own clock, so drop list entries that
    // keep the same time all year. A zone outside the curated list (e.g. set
    // via the API) stays selectable.
    options() {
      const options = TIME_ZONE_OPTIONS.filter((o) => o.value === this.value || !sameClock(o.value, this.defaultZone, this.at));
      if (this.value && !TIME_ZONE_OPTIONS.some((o) => o.value === this.value)) {
        return [...options, { value: this.value, label: this.zoneName(this.value) }];
      }
      return options;
    },
  },
  methods: {
    zoneName(zone) {
      return zoneLabel(zone);
    },
    pick(zone) {
      this.$emit('input', zone || null);
      this.editing = false;
    },
  },
};
</script>
